/**
 * Shared Hero Pattern Component
 *
 * The red architrave hero section with subtle grid pattern.
 * Used across homepage, leaderboard, and any page with the brand header.
 */

import { ReactNode, useId } from 'react';

interface HeroPatternProps {
  children: ReactNode;
  className?: string;
}

export function HeroPattern({ children, className = '' }: HeroPatternProps) {
  // Generate unique ID to avoid SVG pattern conflicts when multiple instances exist
  const id = useId();
  const patternId = `heroGrid-${id}`;

  return (
    <section
      className={`relative overflow-hidden ${className}`}
      style={{ background: '#8B2332' }}
    >
      {/* Subtle grid pattern - exactly 0.08 opacity */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ opacity: 0.08 }}
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <pattern
            id={patternId}
            width="80"
            height="80"
            patternUnits="userSpaceOnUse"
          >
            <rect
              width="80"
              height="80"
              fill="none"
              stroke="white"
              strokeWidth="1"
            />
            <line
              x1="26.66"
              y1="0"
              x2="26.66"
              y2="80"
              stroke="white"
              strokeWidth="0.5"
              opacity="0.5"
            />
            <line
              x1="53.33"
              y1="0"
              x2="53.33"
              y2="80"
              stroke="white"
              strokeWidth="0.5"
              opacity="0.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>

      {/* Content */}
      <div className="relative z-10">{children}</div>

      {/* Bottom curve transition */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 60"
          fill="none"
          preserveAspectRatio="none"
          className="w-full h-10"
        >
          <path
            d="M0 60L1440 60L1440 30C1200 0 240 0 0 30L0 60Z"
            fill="#F5F0EB"
          />
        </svg>
      </div>
    </section>
  );
}
